import Phaser from 'phaser';
import { SCENE_KEYS, ZONE_NAMES } from '../data/gameConstants';
import { Collectible } from '../objects/Collectible';
import { InteractionZone } from '../objects/InteractionZone';
import { NPC } from '../objects/NPC';
import { SaveSystem } from '../systems/SaveSystem';
import { ZoneCoreSystems } from '../systems/ZoneCoreSystems';
import { DecisionPanel } from '../ui/DecisionPanel';
import { ObjectiveHint } from '../ui/ObjectiveHint';
import { QuestPanel } from '../ui/QuestPanel';
import { addZoneHudHeader } from '../ui/ZoneHudHeader';
import { addZoneMenuButton } from '../ui/zoneMenuButton';
import { SceneTransition } from '../utils/SceneTransition';
import { ZONE4_POSITIONS, Zone4Map } from '../zones/zone4/Zone4Map';
import { Zone4QuestFlow } from '../zones/zone4/Zone4QuestFlow';

export class Zone4SafeRouteScene extends Phaser.Scene {
  private core?: ZoneCoreSystems;
  private questPanel?: QuestPanel;
  private questFlow?: Zone4QuestFlow;
  private keris?: Collectible;
  private objectiveHint?: ObjectiveHint;
  private decisionPanel?: DecisionPanel;

  constructor() {
    super({ key: 'Zone4SafeRouteScene' });
  }

  create(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    const map = new Zone4Map(this);
    const obstacles = map.build();

    this.core = new ZoneCoreSystems(this);
    this.questPanel = new QuestPanel(this);
    this.objectiveHint = new ObjectiveHint(this);
    this.decisionPanel = new DecisionPanel(this);

    this.core.setQuestPanel(this.questPanel);
    this.core.setupHUD(4);
    this.core.setupMobileControls();
    const startPos = SaveSystem.getPlayerStartPosition(
      SCENE_KEYS.ZONE4,
      ZONE4_POSITIONS.SPAWN.x,
      ZONE4_POSITIONS.SPAWN.y,
    );
    SaveSystem.saveProgress(SCENE_KEYS.ZONE4, 4, startPos);
    this.core.initPlayer(startPos.x, startPos.y);
    this.core.setupObstacles(obstacles);

    this.keris = new Collectible(this, ZONE4_POSITIONS.KERIS.x, ZONE4_POSITIONS.KERIS.y, {
      id: 'zone4_keris3',
      label: 'Keris 3',
      type: 'keris_3',
      scoreRuleId: 'zone4_keris3',
      dialogueId: 'zone4_rafflesia_keris3',
      kerisId: 'keris_3',
      startHidden: true,
      interactionRadius: 80,
    });

    const guide = new NPC(this, ZONE4_POSITIONS.GUIDE.x, ZONE4_POSITIONS.GUIDE.y, {
      id: 'zone4_guide',
      name: 'Petugas Evakuasi',
      type: 'petugas',
      interactionRadius: 72,
    });
    guide.setInteractEnabled(false);

    const signInteract = ZONE4_POSITIONS.SIGN_INTERACT;
    const signZone = new InteractionZone(
      this, signInteract.x, signInteract.y, 100, 50,
      { id: 'zone4_sign', type: 'custom', label: 'Baca Papan Petunjuk', interactionRadius: 100 },
    );

    const forkInteract = ZONE4_POSITIONS.FORK_INTERACT;
    const forkZone = new InteractionZone(
      this, forkInteract.x, forkInteract.y, 100, 50,
      { id: 'zone4_fork', type: 'custom', label: 'Pilih Jalur Evakuasi', interactionRadius: 100 },
    );

    const riverZone = new InteractionZone(
      this, ZONE4_POSITIONS.RIVER_ROUTE.x, ZONE4_POSITIONS.RIVER_ROUTE.y, 120, 50,
      { id: 'zone4_river', type: 'custom', label: 'Jalur Tepi Sungai', interactionRadius: 105 },
    );

    const hillZone = new InteractionZone(
      this, ZONE4_POSITIONS.HILL_ROUTE.x, ZONE4_POSITIONS.HILL_ROUTE.y, 100, 60,
      { id: 'zone4_hill', type: 'custom', label: 'Jalur Bukit', interactionRadius: 100 },
    );

    const exitZone = new InteractionZone(
      this, ZONE4_POSITIONS.SAFE_EXIT.x, ZONE4_POSITIONS.SAFE_EXIT.y, 140, 100,
      { id: 'zone4_exit', type: 'custom', label: 'Masuk Titik Kumpul', interactionRadius: 120 },
    );

    this.questFlow = new Zone4QuestFlow(
      this.core, map, guide, this.keris, this.objectiveHint, this.decisionPanel,
    );
    this.questFlow.setInteractionZones({
      sign: signZone, fork: forkZone, river: riverZone, hill: hillZone, exit: exitZone,
    });

    signZone.setOnInteract(() => this.questFlow!.onSignInteract());
    guide.setOnInteract(() => this.questFlow!.onGuideInteract());
    forkZone.setOnInteract(() => this.questFlow!.onForkInteract());
    riverZone.setOnInteract(() => this.questFlow!.onRiverInteract());
    hillZone.setOnInteract(() => this.questFlow!.onHillInteract());
    exitZone.setOnInteract(() => this.questFlow!.onExitInteract());
    this.keris.setOnCollect(() => this.questFlow!.onKerisCollected());

    this.core.registerInteractables([signZone, forkZone, riverZone, guide, hillZone, exitZone, this.keris]);
    this.core.setInteractionGate(() => this.decisionPanel!.isVisible());
    this.buildUI();
    this.questFlow.start();
    SceneTransition.reveal(this);
  }

  private buildUI(): void {
    addZoneHudHeader(this, ZONE_NAMES.ZONE4, 'Persimpangan jalur evakuasi');
    addZoneMenuButton(this);
  }

  update(time: number, delta: number): void {
    this.questFlow?.prepareFrame();
    this.core?.update(time, delta);
    this.keris?.updateGlow();

    const player = this.core?.getPlayer();
    if (player && this.questFlow) {
      this.questFlow.update(player.x, player.y);
    }
  }

  shutdown(): void {
    this.questFlow?.destroy();
    this.core?.destroy();
    this.questFlow = undefined;
    this.core = undefined;
    this.questPanel = undefined;
  }
}
